/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {CommonModule} from '@angular/common';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule, Routes} from '@angular/router';

import {TranslateModule} from '@ngx-translate/core';

import {NotificationBoxComponent} from './information/notifications-box/notifications-box.component';
import {NotificationDetailsComponent} from './information/notifications-details/notification-details.component';
import {NotificationsListComponent} from './information/notifications-list/notifications-list.component';
import {NotificationsService} from './information/notifications.service';
import {CommunicationsCardComponent} from './shared/communications-card/communications-card.component';
import {WorkflowGuard} from './shared/workflow-guard';
import {
  RequestsInProgressTableComponent
} from './workflows/requests/requests-in-progress-table/requests-in-progress-table.component';
import {RequestsInProgressComponent} from './workflows/requests/requests-in-progress/requests-in-progress.component';
import {RequestService} from './workflows/services/request.service';
import {TaskResolver} from './workflows/services/task.resolver';
import {TasksService} from './workflows/services/tasks.service';
import {OnboardingFillFormTaskComponent} from './workflows/tasks/onboarding-fill-form-task/onboarding-fill-form-task.component';
import {TaskViewComponent} from './workflows/tasks/task-view/task-view.component';
import {
  TasksInProgressTableComponent
} from './workflows/tasks/tasks-in-progress-table/tasks-in-progress-table.component';
import {TasksInProgressComponent} from './workflows/tasks/tasks-in-progress/tasks-in-progress.component';
import {WorkflowsComponent} from './workflows/workflows.component';

import {MomentModule} from 'ngx-moment';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {SkeletonElementsModule} from 'skeleton-elements/angular';
import {TeamPlanningModule} from '../+mss/team-planning/team-planning.module';
import {AuthenticationGuard} from '../shared/authentication';
import {ModalModule} from '../shared/modal/modal.module';
import {DateLocalizationPipe} from '../shared/pipes/date-localization.pipe';
import {PipesModule} from '../shared/pipes/pipes.module';
import {TeamsSharedModule} from '../shared/teams/teams-shared.module';
import {UiModule} from '../shared/ui/ui.module';
import {isMobileDevice} from '../shared/utils/browser';
import {WorkflowV2Module} from '../shared/workflow/workflow-v2.module';
import {WorkflowsModule} from '../workflows/workflows.module';

const routes: Routes = [
  {
    path: 'inbox',
    component: WorkflowsComponent,
    children: [
      {
        path: 'tasks',
        component: isMobileDevice() ? TasksInProgressComponent : TasksInProgressTableComponent,
        canActivate: [AuthenticationGuard, WorkflowGuard],
        children: [
          {
            path: ':taskId',
            component: TaskViewComponent,
            resolve: {taskWithFormModel: TaskResolver},
            canActivate: [AuthenticationGuard]
          }
        ]
      },
      {
        path: 'requests',
        component: isMobileDevice() ? RequestsInProgressComponent : RequestsInProgressTableComponent,
        canActivate: [AuthenticationGuard, WorkflowGuard]
      }
    ]
  },
  {
    path: 'notifications',
    component: NotificationBoxComponent,
    canActivate: [AuthenticationGuard],
    children: [
      {
        path: '',
        component: NotificationsListComponent,
        children: [
          {
            path: ':id',
            component: NotificationDetailsComponent
          }
        ]
      }
    ]
  }
];

@NgModule({
  imports: [
    CommonModule,
    MomentModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    UiModule,
    PipesModule,
    ModalModule,
    TeamsSharedModule,
    TeamPlanningModule,
    WorkflowV2Module,
    RouterModule.forChild(routes),
    SkeletonElementsModule,
    TooltipModule,
    WorkflowsModule
  ],
  declarations: [
    WorkflowsComponent,
    CommunicationsCardComponent,
    NotificationBoxComponent,
    NotificationDetailsComponent,
    NotificationsListComponent,
    TaskViewComponent,
    OnboardingFillFormTaskComponent,
    TasksInProgressComponent,
    TasksInProgressTableComponent,
    RequestsInProgressComponent,
    RequestsInProgressTableComponent
  ],
  exports: [
    CommunicationsCardComponent,
    NotificationBoxComponent,
    NotificationsListComponent,
    NotificationDetailsComponent
  ],
  providers: [
    NotificationsService,
    DateLocalizationPipe,
    TasksService,
    RequestService
  ]
})
export class InboxModule {
}
